"use client";

import NoImageSrc from "@/assets/images/no_image.svg";
import CldImage from "@/components/ui/cldImg/CldImage";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { ImageType, ProductInfoTabProps } from "../../types";
import { useState, useCallback, useEffect } from "react";
import { cn } from "@/lib/tailwind";
import AngleLeftIcon from "@/assets/icons/angle-left.svg";
import AngleRightIcon from "@/assets/icons/angle-right.svg";
import Image from "next/image";
import BundleVariantSelector from "./BundleVariantSelector";
import Skeleton from "@/components/ui/skeleton/Skeleton";

interface BundleProductInfoProps extends Omit<ProductInfoTabProps, "data"> {
  data: {
    images: ImageType[];
    productInfo: Array<{
      label: string | undefined;
      value: string | undefined;
    }>;
    isBundle?: boolean;
  };
  bundleVariantsData: Array<{
    id: string;
    index: number;
    title: string;
    image: string;
    productInfo: Array<{ label: string | undefined; value: string | undefined }>;
  }>;
  bundleVariantsLoading?: boolean;
  lang: "ar" | "en";
}

const BundleProductInfo = ({
  data,
  label,
  id,
  altText,
  bundleVariantsData,
  bundleVariantsLoading = false,
  lang,
}: BundleProductInfoProps) => {
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const selectedVariant = bundleVariantsData[selectedVariantIndex];

  const navigateToPreviousVariant = useCallback(() => {
    if (bundleVariantsData.length === 0) return;
    const prevIndex = selectedVariantIndex <= 0 ? bundleVariantsData.length - 1 : selectedVariantIndex - 1;
    setSelectedVariantIndex(prevIndex);
  }, [bundleVariantsData.length, selectedVariantIndex]);

  const navigateToNextVariant = useCallback(() => {
    if (bundleVariantsData.length === 0) return;
    const nextIndex = selectedVariantIndex >= bundleVariantsData.length - 1 ? 0 : selectedVariantIndex + 1;
    setSelectedVariantIndex(nextIndex);
  }, [bundleVariantsData.length, selectedVariantIndex]);

  const handleVariantChange = useCallback(
    (variantId: string) => {
      const index = bundleVariantsData.findIndex((v) => v.id === variantId);
      if (index >= 0) {
        setSelectedVariantIndex(index);
      }
    },
    [bundleVariantsData],
  );

  if (bundleVariantsLoading) {
    return (
      <div id={id} className="flex h-full w-full flex-col">
        <div className="w-full bg-[#FAFAFA] py-4">
          <Skeleton className="h-10 w-full bg-gray-200" />
        </div>
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 max-md:py-5 lg:flex-row">
          <div className="flex flex-1 flex-col items-center gap-6 py-5 font-semibold max-sm:ms-5">
            <div className="w-full">
              <Skeleton className="mb-4 hidden h-6 w-32 bg-gray-200 md:block" />
              <Skeleton className="mb-4 h-5 w-48 bg-gray-200" />
              <div className="flex flex-col gap-[1.125rem] max-md:text-sm">
                {[...Array(5)].map((_, idx) => (
                  <div key={idx} className="flex flex-wrap items-start gap-0 lg:gap-10">
                    <Skeleton className="h-4 w-[10rem] bg-gray-200" />
                    <Skeleton className="h-4 w-32 bg-gray-200" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="relative flex h-[9.75rem] w-full flex-1 items-center justify-center md:h-[30.625rem] md:w-[62.75rem]">
          <Skeleton className="h-full w-full bg-gray-200" />
        </div>
      </div>
    );
  }

  if (!selectedVariant || bundleVariantsData.length === 0) {
    return (
      <div id={id} className="flex h-full w-full items-center justify-center">
        <div>No bundle variants found</div>
      </div>
    );
  }

  const displayImage = getCloudinaryURL(selectedVariant.image) ?? NoImageSrc.src;
  const displayProductInfo = selectedVariant.productInfo;

  return (
    <div className="flex h-full w-full flex-col px-0 xl:px-12">
      {bundleVariantsData.length > 0 && (
        <div className="w-full bg-[#FAFAFA] py-4">
          <BundleVariantSelector
            variants={bundleVariantsData.map((v) => ({
              id: v.id,
              name: v.title,
              title: v.title,
            }))}
            selectedVariantId={selectedVariant.id}
            onVariantChange={handleVariantChange}
            className="bg-transparent"
          />
        </div>
      )}

      <div
        id={id}
        className="flex flex-col-reverse items-start justify-between max-md:py-5 lg:flex-row"
      >
        <div className="flex flex-1 flex-col py-5 px-4 md:px-0">
          <div>
            <h2 className="mb-1 hidden text-base font-bold text-black md:block">{label}</h2>
            {selectedVariant?.title && (
              <h3 className="mb-8 text-base font-semibold text-black md:font-normal md:text-gray-500">
                {selectedVariant.title}
              </h3>
            )}
            <div className="flex flex-col gap-4 text-sm md:text-base">
              {displayProductInfo?.map(
                (item, idx) =>
                  item.label != undefined &&
                  item.value != undefined &&
                  String(item.value ?? "").toLowerCase() !== "no" &&
                  item.value !== "كلا" &&
                  item.value !== "-" && (
                    <div key={idx} className="flex items-start gap-4 lg:gap-10">
                      <span className="min-w-[7rem] font-bold capitalize md:min-w-[10rem]">
                        {item.label}
                      </span>
                      <span className="flex items-center gap-2 font-medium">
                        <span className="h-1 w-1 flex-shrink-0 rounded-full bg-black"></span>
                        <span>{item.value}</span>
                      </span>
                    </div>
                  ),
              )}
            </div>
          </div>
        </div>
        <div className="relative flex h-[16rem] w-full flex-1 items-center justify-center md:h-[30.625rem]">
            {bundleVariantsData.length > 1 && (
              <div className="absolute bottom-5 left-5 z-10 hidden items-center gap-3 md:flex" dir="ltr">
                <button
                  className="flex items-center bg-[#F3EEE8] p-2.5 transition-colors hover:bg-[#E5DFD8] active:bg-[#F3EEE8]"
                  onClick={navigateToPreviousVariant}
                  aria-label={lang === "en" ? "Previous variant" : "النسخة السابقة"}
                >
                  <Image
                    src={AngleLeftIcon}
                    alt={lang === "en" ? "Previous" : "سابق"}
                    width={20}
                    height={20}
                    className="h-3.5 w-3.5"
                  />
                </button>
                <button
                  className="flex items-center justify-center bg-[#F3EEE8] p-2.5 transition-colors hover:bg-[#E5DFD8] active:bg-[#F3EEE8]"
                  onClick={navigateToNextVariant}
                  aria-label={lang === "en" ? "Next variant" : "النسخة التالية"}
                >
                  <Image
                    src={AngleRightIcon}
                    alt={lang === "en" ? "Next" : "التالي"}
                    width={20}
                    height={20}
                    className="h-3.5 w-3.5"
                  />
                </button>
              </div>
            )}
            {bundleVariantsData.length > 1 && (
              <div className="absolute bottom-4 left-0 right-0 z-10 flex transform justify-center gap-2 duration-300 ease-in-out md:hidden">
                {bundleVariantsData.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedVariantIndex(i)}
                    className={cn(
                      "h-2 w-2 rounded-full transition-all",
                      selectedVariantIndex === i ? "bg-black" : "bg-gray-300",
                    )}
                    aria-label={`Go to variant ${i + 1}`}
                  />
                ))}
              </div>
            )}
            <CldImage
              src={displayImage}
              alt={altText}
              width={708}
              height={608}
              className="invert-[2%]"
            />
          </div>
        </div>
      </div>
  );
};

export default BundleProductInfo;